import React from 'react';
import { Outlet, useParams } from 'react-router';

import { BackofficePermissionRoute } from '@/backoffice/app/protected-route/permission-route';
import {
  MainContainer,
  RootContainer,
} from '@/backoffice/app/routes/root/root.styled';
import { BackofficeSidebar } from '@/backoffice/features/backoffice-sidebar/backoffice-sidebar';
import { TenantLoginLoader } from '@/backoffice/features/tenants/components/tenant-login-loader/tenant-login-loader';
import { backofficeApi } from '@/backoffice/redux/api/backoffice/backoffice-api';
import { MaintenanceAlert } from '@/shared/components/ui/alert/variant/maintenance-alert';
import { OverlayLoader } from '@/shared/components/ui/overlay-loader/overlay-loader';
import { Header } from '@/shared/features/header/header';
import { useAppTheme } from '@/shared/features/theme-switcher/context/theme-context';
import { useNavigationLoader } from '@/shared/hooks/use-navigation-loader/use-navigation-loader';
import { useUiPreferencesSync } from '@/shared/hooks/use-ui-preferences-sync/use-ui-preferences-sync';
import {
  selectBackofficeTenantIdToken,
  selectUser,
} from '@/shared/redux/api/auth/auth-api';
import { useAppSelector } from '@/shared/redux/hooks/hooks';
import store from '@/shared/redux/store/store';

export const rootLoader = async () => {
  if (!selectUser(store.getState())) {
    return null;
  }

  void store.dispatch(
    backofficeApi.endpoints.ReadSuperUserUiPreferences.initiate(),
  );
  return null;
};

export const AppRoot = () => {
  const displayNavigationLoader = useNavigationLoader();
  const { tenantId } = useParams();
  const tenantIdToken = useAppSelector(selectBackofficeTenantIdToken);
  const { selectedTheme, setSelectedTheme } = useAppTheme();
  useUiPreferencesSync({ theme: selectedTheme, setTheme: setSelectedTheme });

  // The tenant is readable before it is loginable, so the crumb would name a
  // tenant we never got into. No impersonation token means no tenant context.
  const isImpersonationMissing = Boolean(tenantId) && !tenantIdToken;

  return (
    <RootContainer>
      <BackofficeSidebar />
      <MainContainer>
        <OverlayLoader display={displayNavigationLoader} />
        <Header
          hideAlerts
          hideNotifications
          hideTrialExpiredBadge
          hideBreadcrumbs={isImpersonationMissing}
        />
        <MaintenanceAlert />
        {/* Inside the shell so a failed tenant login leaves the sidebar usable. */}
        <TenantLoginLoader>
          <BackofficePermissionRoute>
            <Outlet />
          </BackofficePermissionRoute>
        </TenantLoginLoader>
      </MainContainer>
    </RootContainer>
  );
};
